import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function AdminWithdrawals() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/withdrawals", { params: status ? { status } : {} })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const act = async (id, s, admin_note = null) => {
    try {
      await api.patch(`/admin/withdrawals/${id}`, { status: s, admin_note });
      toast.success(t("common.saved"));
      setRejectingId(null);
      setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-withdrawals-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP2.withdrawals")}</h1>

      <div className="mt-6 flex gap-2">
        {["", "pending", "paid", "rejected"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${
              status === s ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"
            }`}
            data-testid={`wd-filter-${s || "all"}`}
          >
            {s ? t(`agentHub.statuses.${s}`) : t("adminP2.all")}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        {loading ? (
          <div className="text-[#4a6e77]">{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-[#e5ebeb] bg-white p-10 text-center text-[#4a6e77]">
            {t("common.empty")}
          </div>
        ) : (
          <table className="w-full text-sm border-collapse" data-testid="admin-withdrawals-table">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Agent</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Amount</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Bank</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Status</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]">Date</th>
              <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
            </tr></thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id} data-testid={`admin-wd-${w.id}`}>
                  <td className="py-3 px-4 border-b border-[#e5ebeb]">
                    <div className="text-[#003645] font-medium">{w.agent_name}</div>
                    <div className="text-xs text-[#4a6e77]">{w.agent_email}</div>
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#005870] font-semibold">{formatRM(w.amount)}</td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-xs text-[#4a6e77]">
                    <div>{w.bank_snapshot?.bank_name_agent}</div>
                    <div>{w.bank_snapshot?.bank_holder}</div>
                    <div className="font-mono">{w.bank_snapshot?.bank_number_agent}</div>
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb]">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      w.status === "paid" ? "bg-[#00868e]/10 text-[#00868e]" :
                      w.status === "rejected" ? "bg-red-50 text-red-700" :
                      "bg-amber-50 text-amber-700"
                    }`}>
                      {t(`agentHub.statuses.${w.status}`)}
                    </span>
                    {w.admin_note && <div className="mt-1 text-xs text-[#4a6e77]">{w.admin_note}</div>}
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77] text-xs">
                    {new Date(w.created_at).toLocaleString()}
                  </td>
                  <td className="py-3 px-4 border-b border-[#e5ebeb]">
                    {w.status === "pending" && rejectingId !== w.id && (
                      <div className="flex gap-2">
                        <button onClick={() => act(w.id, "paid")} className="p-1.5 rounded-full bg-[#005870] text-white hover:bg-[#003645]" title={t("adminP2.markPaid")} data-testid={`wd-approve-${w.id}`}>
                          <Check size={14} />
                        </button>
                        <button onClick={() => setRejectingId(w.id)} className="p-1.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50" title={t("adminP2.reject")} data-testid={`wd-reject-open-${w.id}`}>
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    {rejectingId === w.id && (
                      <div className="space-y-2">
                        <input placeholder="Reason" value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-2 py-1 rounded border border-[#e5ebeb] text-xs" data-testid={`wd-reject-note-${w.id}`} />
                        <div className="flex gap-1">
                          <button onClick={() => act(w.id, "rejected", note)} className="px-2 py-1 rounded-full bg-red-600 text-white text-xs" data-testid={`wd-reject-confirm-${w.id}`}>{t("common.confirm")}</button>
                          <button onClick={() => { setRejectingId(null); setNote(""); }} className="px-2 py-1 rounded-full border border-[#e5ebeb] text-xs">{t("common.cancel")}</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
